import React, { useState } from 'react';
import { useModalDismiss } from '../../../hooks/useModalDismiss';
import { notifyWarn } from '../../../utils/notify';
import { type Book } from '../../../types';

interface EditBookModalProps {
  book: Book;
  isCreating?: boolean;
  onClose: () => void;
  onSave: (data: { id: string; title: string; description: string; cover: string }) => void;
}

export function EditBookModal({ book, isCreating = false, onClose, onSave }: EditBookModalProps) {
  const [title, setTitle] = useState(book.title);
  const [description, setDescription] = useState(book.description);
  const [cover, setCover] = useState(book.cover);
  const [coverPreview, setCoverPreview] = useState(book.cover);
  const dismiss = useModalDismiss(onClose);

  const handleCoverChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const validTypes = ['image/jpeg', 'image/png'];
      if (!validTypes.includes(file.type)) {
        notifyWarn('仅支持 JPG 和 PNG 格式的图片');
        return;
      }
      const reader = new FileReader();
      reader.onload = (event) => {
        const result = event.target?.result as string;
        setCover(result);
        setCoverPreview(result);
      };
      reader.readAsDataURL(file);
    }
  };

  return (
    <div className="edit-book-modal-overlay" {...dismiss}>
      <div className="edit-book-modal">
        <h2 className="edit-book-modal-title">{isCreating ? '开启新的篇章' : '作品设置'}</h2>
        <div className="edit-book-modal-content">
          {/* 左侧：封面 */}
          <div className="edit-book-cover-section">
            <label className="edit-book-label">作品封面</label>
            <div className="edit-book-cover-upload">
              {coverPreview ? (
                <img src={coverPreview} alt="preview" className="edit-book-cover-preview" />
              ) : (
                <div className="edit-book-cover-placeholder">
                  <span>📘</span>
                  <span>点击上传</span>
                </div>
              )}
              <input
                type="file"
                accept="image/jpeg,image/png"
                onChange={handleCoverChange}
                className="edit-book-cover-input"
              />
            </div>
            <button
              onClick={() => { setCover(''); setCoverPreview(''); }}
              className="edit-book-reset-btn"
            >
              重置封面
            </button>
          </div>

          {/* 右侧：文字信息 */}
          <div className="edit-book-info-section">
            <div>
              <label className="edit-book-label">作品名称</label>
              <input
                autoFocus={isCreating}
                value={title}
                onChange={e => setTitle(e.target.value)}
                placeholder={isCreating ? '书名是故事的眼睛...' : ''}
                className="edit-book-input"
              />
            </div>
            <div className="edit-book-description-wrapper">
              <label className="edit-book-label">作品简介</label>
              <textarea
                value={description}
                onChange={e => setDescription(e.target.value)}
                placeholder={isCreating ? '在这里记下初步的构想...' : ''}
                className="edit-book-textarea"
              />
            </div>
          </div>
        </div>
        <div className="edit-book-modal-actions">
          <button onClick={onClose} className="edit-book-cancel-btn">取消</button>
          <button
            onClick={() => onSave({ id: book.id, title, description, cover })}
            disabled={!title.trim()}
            className="edit-book-save-btn"
          >
            {isCreating ? '开启' : '保存'}
          </button>
        </div>
      </div>
    </div>
  );
}
